"use client";

import { Box } from "@mui/material";
import dynamic from "next/dynamic";
import { ReactNode, useEffect, useRef, useState } from "react";
import type { Settings } from "react-slick";
import { slickOverridesSx } from "./slickOverridesSx";
import { HowManyCards } from "./slCarouselResponsivePresets";

const SLCarousel = dynamic(
  () => import("./SLCarousel").then((m) => ({ default: m.SLCarousel })),
  { ssr: false },
);

function scheduleIdle(callback: () => void): () => void {
  if (typeof requestIdleCallback !== "undefined") {
    const id = requestIdleCallback(callback, { timeout: 2000 });
    return () => cancelIdleCallback(id);
  }

  const id = setTimeout(callback, 1);
  return () => clearTimeout(id);
}

export function SLProgressiveCarousel({
  howManyCards,
  carouselItems,
  grid,
  slidesToShow,
  slickSliderSettings,
}: {
  howManyCards: HowManyCards;
  carouselItems: ReactNode[];
  grid: ReactNode;
  slidesToShow?: number;
  slickSliderSettings?: Settings;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showCarousel, setShowCarousel] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let cancelIdle: (() => void) | undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          observer.disconnect();
          cancelIdle = scheduleIdle(() => setShowCarousel(true));
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelIdle?.();
    };
  }, []);

  return (
    <Box ref={containerRef} sx={slickOverridesSx}>
      {showCarousel ? (
        <SLCarousel
          howManyCards={howManyCards}
          items={carouselItems}
          slidesToShow={slidesToShow}
          slickSliderSettings={slickSliderSettings}
        />
      ) : (
        grid
      )}
    </Box>
  );
}
